import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum AthenaClubWorkOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not entered by the user
  itemName = 'itemName', // SKU / ID of the parent item
  description = 'description',
  itemQty = 'itemQty',
  unitPrice = 'unitPrice',
  lineTotal = 'lineTotal', // unitPrice * itemQty, not entered by the user
}

export enum AthenaClubWorkOrderComponentKeys {
  lineNumber = 'lineNumber', // formatted relative to the parent item, e.g. 1.1
  skuId = 'skuId',
  description = 'description',
  quantityPerKit = 'quantityPerKit',
  requiredQuantity = 'requiredQuantity',
}

const addressSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
})

const componentSchema = yup.object().shape({
  lineNumber: yup.string().nullable().optional(),
  skuId: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  quantityPerKit: yup.string().nullable().optional(),
  requiredQuantity: yup.string().nullable().optional(),
})

const lineItemSchema = yup.object().shape({
  itemNumber: yup.number().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
  components: yup.array().of(componentSchema).optional(),
})

export const athenaClubWorkOrderFileDataSchema = yup.object().shape({
  issuer: addressSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: addressSchema,
  vendor: addressSchema,
  items: yup.array().of(lineItemSchema).required(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
})

export type AthenaClubWorkOrderFileData = yup.InferType<typeof athenaClubWorkOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<AthenaClubWorkOrderFileData>

export const athenaClubWorkOrderFileMappings: RequiredFileData = {
  issuer: {
    attn: 'aeb7e89f-e319-41ed-8217-ac52665767c8',
    companyName: '05b7f686-90e0-4670-a2d4-f8c7854832c1',
    addressLine1: 'f6bf1d1a-464c-418c-af6d-e9363ecc8bd1',
    addressLine2: '9dc75f17-4aba-492f-875c-316467ccec65',
    city: 'ecd2d79b-4a73-4ec2-bd7a-fcbd1d348e85',
    state: '1819fd3b-d997-4fb4-9e22-68f0b29ff00a',
    zipCode: '6b14ac51-c7da-4fdd-934e-b4b194367cec',
    country: 'e474e371-c012-43fe-b4f4-ee198c74eec6',
    phoneNumber: '594a12f4-95bb-4d89-9d0b-71878bd36ae8',
    emailAddress: 'e13dfe7c-9931-4f92-920a-5680aae3d978',
  },
  orderNumber: '4f30f18a-7b74-4ddc-b762-f6222d83eddc',
  paymentTerms: 'f0cd6a29-b47d-48a6-bf6d-9ec0406b9eb4',
  freightTerms: '268b239e-7059-494d-9bd6-be6754d51926',
  dateIssued: '7ec7dbf3-f3bf-4cf1-be0b-0da1c2563a5b',
  dateReady: 'de196df7-66cd-48fe-b30c-ae02d922a0e4',
  destination: {
    attn: '72746d9e-0ff4-4fe4-8324-0e03b1b98421',
    companyName: '12e2ca23-64dd-46bc-a27d-1b8a15531b8c',
    addressLine1: '47b894c4-a143-421e-aec3-e685d69d2a78',
    addressLine2: 'dd2a6a5f-0e45-4670-9923-ae50b1b0482f',
    city: 'ae187cc9-0f6e-4e7c-a66c-c6e491267461',
    state: '2a5a1e68-054b-492c-9d72-85e1d2d3a90e',
    zipCode: 'b55463cc-df33-46be-8bbf-cf4b4fb80bda',
    country: '980d4bc2-de52-4010-bcf9-5efb70f504be',
    phoneNumber: '79cf5dd2-3f23-4ee4-96f4-f1a7e1433f73',
    emailAddress: '2a299d1a-c747-4d59-8423-76b3c9d86bea',
  },
  vendor: {
    attn: '30ca7ff6-9cfc-4f6b-a040-6f5beca96a5f',
    companyName: 'a074364a-6048-4d85-bd1d-b01b0db49efc',
    addressLine1: 'f9d7527e-93a7-4a63-b726-91e9f3b29962',
    addressLine2: '3b605856-99ff-49b5-b820-73059b64e01e',
    city: 'f45fe3c0-ddd1-4e3a-9eab-285c44dccb79',
    state: '695de78e-ca14-43e6-afde-98ade4382fdf',
    zipCode: '4b8998a9-5abc-411f-ac32-a345a1758e32',
    country: '8aaf2fd8-39da-4c1f-a2eb-e3113f4570b8',
    phoneNumber: '56036634-03a8-4647-b7d8-97c9678d4163',
    emailAddress: 'a8bd5e7a-2257-46bd-a454-dbf3bd4d820e',
  },
  items: [
    {
      itemNumber: null,
      itemName: 'd429065d-9963-4dfe-99c6-3aa37cb40185',
      description: '2fc5f3ed-1396-44b1-84f1-a6db820c3658',
      itemQty: '32353688-bd52-40f8-8054-6c63fbc496a8',
      unitPrice: '9fc46ba1-d3e6-475f-996a-2f6c2a6a0e31',
      lineTotal: null,
      components: [
        {
          lineNumber: null,
          skuId: 'fa2a4986-04b2-4ed4-8f7d-3b2873c8f3df',
          description: '12981bc5-2376-4734-a208-0338023da5ae',
          quantityPerKit: '438aa619-8fcd-4a20-8482-b7129b673bd9',
          requiredQuantity: 'eb614571-1938-49e2-b013-d9346258411b',
        },
      ],
    },
  ],
  agreement: '37548c60-44d2-4e2a-841f-fc750532050b',
  instructions: '70d66dcb-cda8-482c-b73c-314c0e6ae050',
}
